import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Slider
} from "@mui/material";
import { ProgressButton } from "@qubit/autoparts";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  BoardTextLabelFields,
  DEFAULT_TEXT_LABEL_FONT_SIZE,
  MAX_TEXT_LABEL_FONT_SIZE,
  MIN_TEXT_LABEL_FONT_SIZE
} from "~/features/andon/andon.types";

import { clampTextLabelFontSize } from "~/features/andon/andonBoard/andonBoardTextLabel.utils";

import TempZoneColorPresets from "./TempZoneColorPresets";

type TextLabelModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (textLabel: BoardTextLabelFields) => boolean | void;
  existingElement?: BoardTextLabelFields;
};

const DEFAULT_BACKGROUND_COLOR = "rgba(0, 0, 0, 0.7)";
const DEFAULT_TEXT_COLOR = "#ffffff";

export function TextLabelModal({
  open,
  onClose,
  onSave,
  existingElement
}: TextLabelModalProps) {
  const { t } = useTranslation();

  const [text, setText] = useState("");
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_BACKGROUND_COLOR
  );
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
  const [fontSize, setFontSize] = useState(DEFAULT_TEXT_LABEL_FONT_SIZE);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open) {
      if (existingElement) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
        setText(existingElement.text);
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
        setBackgroundColor(
          existingElement.backgroundColor || DEFAULT_BACKGROUND_COLOR
        );
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
        setTextColor(existingElement.textColor || DEFAULT_TEXT_COLOR);
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
        setFontSize(existingElement.fontSize ?? DEFAULT_TEXT_LABEL_FONT_SIZE);
      } else {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setText("");
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setBackgroundColor(DEFAULT_BACKGROUND_COLOR);
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setTextColor(DEFAULT_TEXT_COLOR);
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setFontSize(DEFAULT_TEXT_LABEL_FONT_SIZE);
      }
    }
  }, [open, existingElement]);

  const handleSave = () => {
    if (!text.trim()) return;

    const textLabel: BoardTextLabelFields = {
      id: existingElement?.id || crypto.randomUUID(),
      text: text.trim(),
      backgroundColor,
      textColor,
      fontSize: clampTextLabelFontSize(fontSize)
    };

    const saved = onSave(textLabel);
    if (saved !== false) {
      onClose();
    }
  };

  const isEditing = !!existingElement;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? t("edit text label") : t("add text label")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
          <TextField
            label={t("label text")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            autoFocus
            placeholder={t("enter label text")}
          />

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("font size")}: {fontSize}px
            </Typography>
            <Slider
              value={fontSize}
              onChange={(_event, value) => setFontSize(value as number)}
              min={MIN_TEXT_LABEL_FONT_SIZE}
              max={MAX_TEXT_LABEL_FONT_SIZE}
              step={1}
              valueLabelDisplay="auto"
              aria-label={t("font size")}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("background color")}
              </Typography>
              <input
                type="color"
                value={
                  backgroundColor.startsWith("rgba")
                    ? "#000000"
                    : backgroundColor
                }
                onChange={(e) => setBackgroundColor(e.target.value)}
                style={{
                  width: "100%",
                  height: 40,
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  borderRadius: 4
                }}
              />
              <Box sx={{ mt: 1 }}>
                <TempZoneColorPresets onSelect={setBackgroundColor} />
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("text color")}
              </Typography>
              <input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                style={{
                  width: "100%",
                  height: 40,
                  cursor: "pointer",
                  border: "1px solid #ccc",
                  borderRadius: 4
                }}
              />
              <Box sx={{ mt: 1 }}>
                <TempZoneColorPresets onSelect={setTextColor} />
              </Box>
            </Box>
          </Box>

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("preview")}
            </Typography>
            <Box
              sx={{
                backgroundColor,
                color: textColor,
                p: 2,
                borderRadius: 1,
                textAlign: "center",
                fontWeight: "bold",
                fontSize: `${fontSize}px`,
                minHeight: 48,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid",
                borderColor: "rgba(255, 255, 255, 0.3)"
              }}
            >
              {text || t("enter label text")}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <ProgressButton
          emphasis="low"
          responsive
          onClick={onClose}
          sx={{ textTransform: "none" }}
        >
          {t("cancel")}
        </ProgressButton>
        <ProgressButton
          emphasis="high"
          responsive
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!text.trim()}
          sx={{ textTransform: "none" }}
        >
          {isEditing ? t("save") : t("add")}
        </ProgressButton>
      </DialogActions>
    </Dialog>
  );
}

export default TextLabelModal;
